/**
 * Invoices dashboard.
 *
 * - Filter by date range (Quick: this week / this month / custom),
 *   status (draft|sent|paid), customer, and free-text (number or name).
 * - Sortable in-page table (date, number, customer, total).
 * - Per-row actions: View PDF, Resend (WhatsApp), Mark sent, Mark paid,
 *   Delete.
 * - Bulk actions on the current filtered set: Download all (one-by-one
 *   open) and CSV export.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { SERVICE_LABELS } from '@fin/shared/enums';
import { buildWhatsAppLink } from '@fin/shared/whatsapp';

import { Card, Button, Field, Modal, inputClass } from '../components/ui';
import {
  useInvoices,
  useUpdateInvoiceStatus,
  useDeleteInvoice,
  fetchInvoiceShareUrl,
  useCustomers,
  useCustomer,
  useJob,
  type Invoice,
  type InvoiceListRow,
} from '../lib/queries';

type SortKey = 'createdAt' | 'number' | 'customerName' | 'totalFils' | 'status';

function dubaiTodayKey(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
}

function fmtAed(fils: number): string {
  return (fils / 100).toLocaleString('en-AE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-AE', {
    timeZone: 'Asia/Dubai',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function statusBadge(status: Invoice['status']) {
  const styles: Record<Invoice['status'], string> = {
    draft: 'bg-stone-200 text-stone-700',
    sent: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styles[status]}`}>
      {status}
    </span>
  );
}

function dateRangePreset(preset: 'all' | 'week' | 'month' | 'year'): { from?: string; to?: string } {
  const now = new Date();
  // Anchor on Dubai date so "this week" doesn't cut off a job at midnight UTC.
  const dubai = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
  if (preset === 'all') return {};
  if (preset === 'week') {
    const start = new Date(dubai);
    const day = start.getDay(); // 0 = Sunday
    start.setDate(start.getDate() - day);
    return { from: start.toISOString().slice(0, 10) };
  }
  if (preset === 'month') {
    const start = new Date(dubai.getFullYear(), dubai.getMonth(), 1);
    return { from: start.toISOString().slice(0, 10) };
  }
  if (preset === 'year') {
    const start = new Date(dubai.getFullYear(), 0, 1);
    return { from: start.toISOString().slice(0, 10) };
  }
  return {};
}

export default function InvoicesPage() {
  const [preset, setPreset] = useState<'all' | 'week' | 'month' | 'year' | 'custom'>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [status, setStatus] = useState<'' | Invoice['status']>('');
  const [customerId, setCustomerId] = useState('');
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [confirmDelete, setConfirmDelete] = useState<InvoiceListRow | null>(null);

  const range = preset === 'custom'
    ? { from: customFrom || undefined, to: customTo || undefined }
    : dateRangePreset(preset);

  const filters = {
    from: range.from,
    to: range.to,
    status: status || undefined,
    customerId: customerId || undefined,
    q: q.trim() || undefined,
    limit: 100,
  } as const;

  const list = useInvoices(filters);
  const customers = useCustomers();
  const updateStatus = useUpdateInvoiceStatus();
  const remove = useDeleteInvoice();

  const items = list.data?.items ?? [];

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const k = sortKey;
      if (k === 'totalFils') return (a.totalFils - b.totalFils) * dir;
      const av = String((a as InvoiceListRow & Record<string, unknown>)[k] ?? '');
      const bv = String((b as InvoiceListRow & Record<string, unknown>)[k] ?? '');
      return av.localeCompare(bv) * dir;
    });
  }, [items, sortKey, sortDir]);

  function flipSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'createdAt' || k === 'totalFils' ? 'desc' : 'asc');
    }
  }

  const totals = useMemo(() => {
    const sub = sorted.reduce((s, i) => s + i.totalFils, 0);
    const paid = sorted.filter((i) => i.status === 'paid').reduce((s, i) => s + i.totalFils, 0);
    const outstanding = sub - paid;
    return { sub, paid, outstanding, count: sorted.length };
  }, [sorted]);

  function downloadCsv() {
    const header = ['Number', 'Date', 'Customer', 'Service', 'Status', 'Subtotal AED', 'VAT AED', 'Total AED'];
    const rows = sorted.map((i) => [
      i.number,
      new Date(i.createdAt).toISOString(),
      i.customerName,
      SERVICE_LABELS[i.serviceType as keyof typeof SERVICE_LABELS]?.en ?? i.serviceType,
      i.status,
      (i.subtotalFils / 100).toFixed(2),
      (i.vatFils / 100).toFixed(2),
      (i.totalFils / 100).toFixed(2),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `invoices-${dubaiTodayKey()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: BRAND.text }}>
            Invoices
          </h1>
          <p className="text-sm text-stone-500">
            Browse, filter, resend or delete invoices across all jobs.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Button variant="white" onClick={downloadCsv} disabled={sorted.length === 0}>
            Download CSV
          </Button>
        </div>
      </header>

      {/* Stat strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Invoices" value={String(totals.count)} />
        <Stat label="Total" value={`AED ${fmtAed(totals.sub)}`} />
        <Stat label="Paid" value={`AED ${fmtAed(totals.paid)}`} accent="emerald" />
        <Stat label="Outstanding" value={`AED ${fmtAed(totals.outstanding)}`} accent="amber" />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Range">
            <div className="flex flex-wrap gap-1">
              {(['week', 'month', 'year', 'all', 'custom'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPreset(p)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize transition ${
                    preset === p
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {p === 'week' ? 'This week' : p === 'month' ? 'This month' : p === 'year' ? 'This year' : p}
                </button>
              ))}
            </div>
          </Field>

          {preset === 'custom' && (
            <>
              <Field label="From">
                <input
                  type="date"
                  className={inputClass}
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </Field>
              <Field label="To">
                <input
                  type="date"
                  className={inputClass}
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </Field>
            </>
          )}

          <Field label="Status">
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
            </select>
          </Field>

          <Field label="Customer">
            <select
              className={inputClass}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">All</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Search">
            <input
              className={inputClass}
              placeholder="Number or customer name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        {list.isPending ? (
          <p className="text-sm text-stone-500">Loading invoices…</p>
        ) : list.error ? (
          <p className="text-sm text-rose-700">
            Failed to load invoices: {(list.error as Error).message}
          </p>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-stone-500">No invoices match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-left text-xs font-semibold uppercase tracking-wider text-stone-500">
                  <Th onClick={() => flipSort('createdAt')} active={sortKey === 'createdAt'} dir={sortDir}>Date</Th>
                  <Th onClick={() => flipSort('number')} active={sortKey === 'number'} dir={sortDir}>Number</Th>
                  <Th onClick={() => flipSort('customerName')} active={sortKey === 'customerName'} dir={sortDir}>Customer</Th>
                  <Th>Service</Th>
                  <Th onClick={() => flipSort('status')} active={sortKey === 'status'} dir={sortDir}>Status</Th>
                  <Th onClick={() => flipSort('totalFils')} active={sortKey === 'totalFils'} dir={sortDir} align="right">
                    Total
                  </Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((inv) => (
                  <tr key={inv.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                    <td className="whitespace-nowrap px-2 py-2 text-xs text-stone-600">{fmtDate(inv.createdAt)}</td>
                    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">
                      <a href={inv.url} target="_blank" rel="noreferrer" className="text-stone-900 hover:underline">
                        {inv.number}
                      </a>
                    </td>
                    <td className="px-2 py-2">
                      <Link to={`/customers/${inv.customerId}`} className="text-stone-900 hover:underline">
                        {inv.customerName || '—'}
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-stone-600">
                      {SERVICE_LABELS[inv.serviceType as keyof typeof SERVICE_LABELS]?.en ?? inv.serviceType}
                    </td>
                    <td className="px-2 py-2">{statusBadge(inv.status)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-right font-semibold">
                      AED {fmtAed(inv.totalFils)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right">
                      <RowActions
                        inv={inv}
                        onMarkSent={() => updateStatus.mutate({ id: inv.id, status: 'sent' })}
                        onMarkPaid={() => updateStatus.mutate({ id: inv.id, status: 'paid' })}
                        onDelete={() => setConfirmDelete(inv)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete invoice?"
      >
        <div className="space-y-4 text-sm text-stone-700">
          <p>
            This will permanently remove invoice <strong>{confirmDelete?.number}</strong> for{' '}
            <strong>{confirmDelete?.customerName || 'unknown customer'}</strong> and the underlying PDF.
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="text-sm text-stone-500 hover:underline"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await remove.mutateAsync(confirmDelete.id);
                  setConfirmDelete(null);
                } catch (err) {
                  alert(err instanceof Error ? err.message : String(err));
                }
              }}
              className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
            >
              {remove.isPending ? 'Deleting…' : 'Delete invoice'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: 'emerald' | 'amber' }) {
  const accentColor =
    accent === 'emerald' ? '#047857' : accent === 'amber' ? '#b45309' : BRAND.text;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{label}</p>
      <p className="mt-1 text-lg font-semibold" style={{ color: accentColor }}>
        {value}
      </p>
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  align,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  dir?: 'asc' | 'desc';
  align?: 'right';
}) {
  const cls = `px-2 py-2 ${align === 'right' ? 'text-right' : ''} ${onClick ? 'cursor-pointer select-none hover:text-stone-700' : ''}`;
  return (
    <th onClick={onClick} className={cls}>
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
}

function RowActions({
  inv,
  onMarkSent,
  onMarkPaid,
  onDelete,
}: {
  inv: InvoiceListRow;
  onMarkSent: () => void;
  onMarkPaid: () => void;
  onDelete: () => void;
}) {
  const [busy, setBusy] = useState(false);
  // Look up customer + job lazily on hover/click via the existing single
  // queries; cached in React Query so subsequent rows are cheap.
  const customer = useCustomer(inv.customerId);
  const job = useJob(inv.jobId);

  async function shareWhatsApp() {
    if (!customer.data || !job.data) {
      alert('Loading customer/job details — try again in a moment.');
      return;
    }
    setBusy(true);
    try {
      const fresh = await fetchInvoiceShareUrl(inv.id);
      const link = buildWhatsAppLink(
        customer.data.whatsappPhone ?? customer.data.phone,
        'customer_invoice',
        {
          customerName: customer.data.name,
          serviceType:
            SERVICE_LABELS[inv.serviceType as keyof typeof SERVICE_LABELS]?.en ?? inv.serviceType,
          invoiceNumber: fresh.number,
          invoiceTotal: `AED ${fmtAed(fresh.totalFils)}`,
          invoiceUrl: fresh.shortUrl ?? fresh.url,
        },
        'en',
      );
      if (link) {
        window.open(link, '_blank', 'noopener');
        if (inv.status === 'draft') onMarkSent();
      } else {
        alert('Customer is missing a WhatsApp phone number.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inline-flex flex-wrap justify-end gap-1 text-xs">
      <a
        href={inv.url}
        target="_blank"
        rel="noreferrer"
        className="rounded border border-stone-300 px-2 py-1 hover:bg-stone-100"
      >
        PDF
      </a>
      <button
        type="button"
        disabled={busy}
        onClick={shareWhatsApp}
        className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
        title="Send to customer via WhatsApp"
      >
        {inv.status === 'sent' ? 'Resend' : 'Send'}
      </button>
      {inv.status !== 'paid' && (
        <button
          type="button"
          onClick={onMarkPaid}
          className="rounded border border-stone-300 px-2 py-1 hover:bg-stone-100"
        >
          Mark paid
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:bg-rose-100"
      >
        Delete
      </button>
    </div>
  );
}
